import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { DispatchStatus } from "../types/dispatch";

export interface ToastNotification {
  id: string;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
  duration?: number;
}

type ToastContextType = {
  notifications: ToastNotification[];
  addNotification: (n: Omit<ToastNotification, "id">) => string;
  removeNotification: (id: string) => void;
  showStatusUpdateSuccess: (status: DispatchStatus) => string;
  showStatusUpdateError: (error: string) => string;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback(
    (notification: Omit<ToastNotification, "id">) => {
      const id = Date.now().toString();
      const newNotification: ToastNotification = {
        id,
        duration: 4000,
        ...notification,
      };
      setNotifications((prev) => [...prev, newNotification]);
      // Auto remove
      setTimeout(() => removeNotification(id), newNotification.duration);
      return id;
    },
    [removeNotification]
  );

  const showStatusUpdateSuccess = useCallback(
    (status: DispatchStatus) => {
      const statusLabels: Record<DispatchStatus, string> = {
        pending: "🕐 Pending",
        confirmed: "✅ Confirmed",
        packed: "📦 Packed",
        shipped: "🚚 Shipped",
        in_transit: "🛣️ In Transit",
        out_for_delivery: "🏃 Out for Delivery",
        delivered: "✅ Delivered",
        failed: "❌ Failed",
        returned: "↩️ Returned",
        cancelled: "🚫 Cancelled",
      };
      return addNotification({
        type: "success",
        title: "Status Updated Successfully!",
        message: `Dispatch status changed to ${statusLabels[status]}`,
      });
    },
    [addNotification]
  );

  const showStatusUpdateError = useCallback(
    (error: string) => {
      return addNotification({
        type: "error",
        title: "Status Update Failed",
        message: error || "Failed to update dispatch status. Please try again.",
      });
    },
    [addNotification]
  );

  const value = useMemo<ToastContextType>(
    () => ({
      notifications,
      addNotification,
      removeNotification,
      showStatusUpdateSuccess,
      showStatusUpdateError,
    }),
    [
      notifications,
      addNotification,
      removeNotification,
      showStatusUpdateError,
      showStatusUpdateSuccess,
    ]
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};

export const useToastNotifications = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToastNotifications must be used within a ToastProvider"
    );
  }
  return ctx;
};

export default useToastNotifications;
